import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import DefaultRadioButton from '@images/svg-icons/defaultRadioButton.svg';
import RadioButton from '@images/svg-icons/radioButton.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';

import { Checkbox, FormControlLabel } from '@mui/material';

import Modal from '@components/styled-UI/Modal';

import { ICreditCardForm, IPaymentMethodDetails } from '@interfaces/myProfileInterfaces';

import { removePaymentMethod, setDefaultPaymentMethod } from '@services/myProfileServices';

import { getLabelFallback } from '@utils/utils';
import { CREDIT_CARDS_LOGOS } from '@utils/pages-utils/MyProfileUtils';
import EditCreditCardForm from './EditCreditCardForm';

const PaymentMethodToCcForm = (pm: IPaymentMethodDetails): ICreditCardForm => ({
    address: pm.billTo_street1,
    city: pm.billTo_city,
    country: pm.billTo_country,
    creditCardNumber: pm.card_accountNumber.replace(/X/g, '*'),
    email: pm.billTo_email,
    firstname: pm.firstName,
    lastname: pm.lastName,
    state: pm.billTo_state,
    zipcode: pm.billTo_postalCode,
    cvv: '***',
    expirationDate: `${pm.expireMonth}/${pm.expireYear.slice(-2)}`,
    recaptchaResponse: '',
});

interface Props {
    paymentMethodDetails: IPaymentMethodDetails;
    index: number;
    defaultPaymentMethod: number;
}

const PaymentMethodDetails = ({
    paymentMethodDetails,
    index,
    defaultPaymentMethod,
}: Props): JSX.Element => {
    const { firstName, lastName, ccBrand, lastFourDigits, expireMonth, expireYear } =
        paymentMethodDetails;

    // new Date() returns a Date instance for the current date
    // due to how JS handles dates, new Date(+expireYear, +expireMonth) returns
    // a Date instance for the first day the card becomes invalid
    const isExpired = new Date(+expireYear, +expireMonth) <= new Date();

    const [isDefaultPaymentMethod, setIsDefaultPaymentMethod] = useState<boolean>(
        index === defaultPaymentMethod
    );

    const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);

    const config = useSelector((state: RootState) => state.common.config);
    const isMobile = useSelector((state: RootState) => state.common.isMobile);

    const [isEditFormVisible, setIsEditFormVisible] = useState(false);

    const dispatch = useDispatch();

    const handleSetDefaultPaymentMethod = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setIsDefaultPaymentMethod(event.target.checked);
            dispatch(setDefaultPaymentMethod(config.mockRequest, config.storeId, index));
        }
    };

    const capitalizeWords = (str) => {
        return str
            ?.toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    return (
        <>
            {isEditFormVisible ? (
                <EditCreditCardForm
                    defaultValues={PaymentMethodToCcForm(paymentMethodDetails)}
                    paymentId={index}
                />
            ) : (
                <>
                    <div className={`payment-method-details ${isExpired ? 'expired' : ''}`}>
                        <div
                            role="region"
                            aria-label="My payment methods details header"
                            className="header"
                        >
                            <span className="primary-message medium align-left bold">
                                {getLabelFallback('PAYMENT_METHOD_DETAIL_TITLE')}
                            </span>
                            {isExpired ? (
                                <button
                                    className="remove-button"
                                    onClick={() => setIsOpenDeleteModal(true)}
                                >
                                    Remove
                                </button>
                            ) : (
                                <button
                                    className="primary-button-negative black button-size-small"
                                    onClick={() => setIsEditFormVisible(true)}
                                >
                                    {getLabelFallback('PAYMENT_METHOD_DETAIL_EDIT_CTA_LABEL')}
                                </button>
                            )}
                        </div>
                        <div
                            role="region"
                            aria-label="My payment methods details body"
                            className="body"
                        >
                            <div className="primary-message medium align-left bold mb-10">
                                {capitalizeWords(firstName)} {capitalizeWords(lastName)}
                            </div>
                            <div className="credit-card-informations">
                                <div className="card-type">
                                    {
                                        CREDIT_CARDS_LOGOS[
                                            ccBrand.toLocaleUpperCase().replace(/\s/g, '')
                                        ]
                                    }
                                    <div className="primary-message medium align-left">
                                        {getLabelFallback(
                                            'PAYMENT_METHOD_DETAILS_LAST_FOUR_DIGITS_MESSAGE'
                                        ).replace('{LAST_FOUR_DIGITS}', lastFourDigits)}
                                    </div>
                                </div>
                                <div className="primary-message medium align-left">
                                    {getLabelFallback(
                                        'PAYMENT_METHOD_DETAILS_EXPIRATION_DATE_MESSAGE'
                                    ).replace('{EXPIRATION_DATE}', `${expireMonth}/${expireYear}`)}
                                </div>
                            </div>
                        </div>
                        <div
                            role="region"
                            aria-label="My payment methods details footer"
                            className="footer"
                        >
                            {isExpired ? (
                                <div className="expired-message">
                                    <ErrorIcon />
                                    {getLabelFallback('PAYMENT_METHOD_DETAILS_EXPIRED_MESSAGE')}
                                </div>
                            ) : (
                                <>
                                    <FormControlLabel
                                        classes={{
                                            root: 'default-payment-method',
                                            label: 'primary-message medium',
                                        }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                sx={{
                                                    color: 'black',
                                                    '&.Mui-checked': {
                                                        color: 'black',
                                                    },
                                                }}
                                                icon={<DefaultRadioButton />}
                                                checkedIcon={<RadioButton />}
                                                checked={isDefaultPaymentMethod}
                                                onChange={handleSetDefaultPaymentMethod}
                                            />
                                        }
                                        label={getLabelFallback(
                                            isDefaultPaymentMethod
                                                ? 'PAYMENT_METHOD_DETAILS_DEFAULT_MESSAGE'
                                                : 'PAYMENT_METHOD_DETAILS_SET_AS_DEFAULT_MESSAGE'
                                        )}
                                    />
                                    <button
                                        className="remove-button"
                                        onClick={() => setIsOpenDeleteModal(true)}
                                    >
                                        Remove
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <Modal
                        customStyle={isMobile ? 'payment-methods-sm' : ''}
                        isOpen={isOpenDeleteModal}
                        close={() => setIsOpenDeleteModal(false)}
                        isCentered
                    >
                        <div className="payment-methods-delete-modal">
                            <div
                                role="region"
                                aria-label="My payment methods delete modal title"
                                className="title"
                            >
                                {getLabelFallback('PAYMENT_METHOD_DELETE_MODAL_TITLE')}
                            </div>
                            <div className="buttons-container">
                                <button
                                    role="region"
                                    aria-label="My payment methods delete modal remove button"
                                    data-element-id="PaymentMethodOverlay_Remove"
                                    data-description="Confirm"
                                    className="primary-button button-size-medium"
                                    onClick={() => {
                                        dispatch(
                                            removePaymentMethod(
                                                config.mockRequest,
                                                config.storeId,
                                                index
                                            )
                                        );
                                        setIsOpenDeleteModal(false);
                                    }}
                                >
                                    {getLabelFallback(
                                        'PAYMENT_METHOD_DELETE_MODAL_REMOVE_CTA_LABEL'
                                    )}
                                </button>
                                <button
                                    role="region"
                                    aria-label="My payment methods delete modal cancel button"
                                    data-element-id="PaymentMethodsOverlay_Remove"
                                    data-description="Close"
                                    className="primary-button-negative button-size-medium"
                                    onClick={() => setIsOpenDeleteModal(false)}
                                >
                                    {getLabelFallback(
                                        'PAYMENT_METHOD_DELETE_MODAL_CLOSE_CTA_LABEL'
                                    )}
                                </button>
                            </div>
                        </div>
                    </Modal>
                </>
            )}
        </>
    );
};

export default PaymentMethodDetails;
